// src/components/Layout.js
import React, { useRef, useEffect, useState } from "react";
import { Menubar } from "primereact/menubar";
import { TieredMenu } from "primereact/tieredmenu";
import SidebarMenu from "./SidebarMenu";
import { Button } from "primereact/button";
import {
  useNavigate,
  Outlet,
  Link,
} from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useTheme } from "../contexts/ThemeContext";
import { fetchUserPermissions } from "../api/userApi";
import { hasPermission } from "../utils/permissions";
import NotificationBell from "./Notifications/NotificationBell";
import ImpersonationBar from "./ImpersonationBar";
import TrialBanner from "./TrialBanner";
import { useSchoolSettings } from "../utils/schoolSettings";
import "../styles/layout.scss";
const Layout = () => {
  const [permissions, setPermissions] = useState([]);
  const { logout, user, impersonatedSchool, exitSchool, impersonatedUser, stopImpersonation } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  // Schools that upload their own logo see it here instead of the product mark.
  const { logo: schoolLogo } = useSchoolSettings();
  const isSuperAdminImpersonating = user?.roleName === 'SuperAdmin' && !!impersonatedSchool;
  const isUserImpersonation = isSuperAdminImpersonating && !!impersonatedUser;

  useEffect(() => {
    if (isSuperAdminImpersonating && !isUserImpersonation) {
      // SuperAdmin in school mode without user impersonation — sees everything
      setPermissions([{ action: 'manage', subject: 'all' }]);
      return;
    }
    // When impersonating a user, fetch that user's permissions
    const targetUserId = isUserImpersonation ? impersonatedUser.id : user.id;
    const getPermissions = async () => {
      try {
        const userPermissions = await fetchUserPermissions(targetUserId);
        setPermissions(userPermissions);
      } catch (error) {
        console.error("Error fetching permissions:", error);
      }
    };

    getPermissions();
  }, [isSuperAdminImpersonating, isUserImpersonation, impersonatedUser?.id]);

  const menuRef = useRef(null);
  const navigate = useNavigate();

  const handleExitSchool = () => {
    exitSchool();
    navigate('/platform/dashboard');
  };

  const handleStopUserImpersonation = () => {
    stopImpersonation();
  };
  // When impersonating a user, use that user's role for menu visibility
  const effectiveRole = isUserImpersonation
    ? impersonatedUser.roleName
    : (user?.roleName || user?.role?.name || user?.Role?.name);
  const isParent = effectiveRole === "Parent";
  const isAdmin = isUserImpersonation
    ? effectiveRole === "Admin"
    : (effectiveRole === "Admin" || effectiveRole === "SuperAdmin" || isSuperAdminImpersonating);
  const isTeacher = effectiveRole === "Teacher";
  const canSeeWebsite = ["SuperAdmin", "Admin", "Management"].includes(effectiveRole);

  const menuItems = [
    // Dashboard - Primary entry point (teachers get their own portal)
    {
      label: "Dashboard",
      icon: "pi pi-fw pi-th-large",
      path: isTeacher ? "/index/teacher/dashboard" : "/index/dashboard",
      command: () => navigate(isTeacher ? '/index/teacher/dashboard' : '/index/dashboard'),
      visible: isTeacher || hasPermission(permissions, "Dashboard", "read"),
    },

    // Parent Portal — only visible to Parent role
    {
      label: "Parent Portal",
      icon: "pi pi-fw pi-heart",
      visible: isParent,
      items: [
        {
          label: "My Children",
          icon: "pi pi-fw pi-users",
          path: "/index/parent/dashboard",
          command: () => navigate("/index/parent/dashboard"),
          visible: isParent,
        },
      ],
    },

    // Messages — visible to all authenticated users
    {
      label: "Messages",
      icon: "pi pi-fw pi-comments",
      path: "/index/messages",
      command: () => navigate("/index/messages"),
      visible: true,
    },

    // Announcements — admin creates; teachers/parents view
    {
      label: "Announcements",
      icon: "pi pi-fw pi-megaphone",
      path: "/index/announcements",
      command: () => navigate("/index/announcements"),
      visible: isAdmin || isTeacher || isParent,
    },

    // Notifications
    {
      label: "Notifications",
      icon: "pi pi-fw pi-bell",
      visible: true,
      items: [
        {
          label: "All Notifications",
          icon: "pi pi-fw pi-list",
          path: "/index/notifications",
          command: () => navigate("/index/notifications"),
          visible: true,
        },
        {
          label: "Settings",
          icon: "pi pi-fw pi-cog",
          path: "/index/notification-preferences",
          command: () => navigate("/index/notification-preferences"),
          visible: isAdmin || hasPermission(permissions, "Setting", "manage"),
        },
      ],
    },

    // Academic — Assignments / Exams / Results
    {
      label: "Academic Activities",
      icon: "pi pi-fw pi-pencil",
      visible: isAdmin || isTeacher || isParent,
      items: [
        {
          label: "Assignments",
          icon: "pi pi-fw pi-file-edit",
          path: "/index/assignments",
          command: () => navigate("/index/assignments"),
          visible: true,
        },
        {
          label: "Exams",
          icon: "pi pi-fw pi-book",
          path: "/index/exams",
          command: () => navigate("/index/exams"),
          visible: isAdmin || isTeacher,
        },
        {
          label: "Report Card",
          icon: "pi pi-fw pi-chart-line",
          path: "/index/report-card",
          command: () => navigate("/index/report-card"),
          visible: isParent || isAdmin || isTeacher,
        },
      ],
    },

    // Student Management - Core daily operations for office admins
    {
      label: "Student Management",
      icon: "pi pi-fw pi-graduation-cap",
      visible: hasPermission(permissions, "Student", "read") || hasPermission(permissions, "Enrollment", "read"),
      items: [
        {
          label: isParent ? "Children" : "Students",
          icon: "pi pi-fw pi-users",
          path: "/index/student",
          command: () => navigate("/index/student"),
          visible: hasPermission(permissions, "Student", "read"),
        },
        {
          label: "Enrollments",
          icon: "pi pi-fw pi-user-plus",
          path: "/index/enrollments",
          command: () => navigate("/index/enrollments"),
          visible: hasPermission(permissions, "Enrollment", "read"),
        },
        {
          label: "Promotions",
          icon: "pi pi-fw pi-arrow-circle-up",
          path: "/index/promotions",
          command: () => navigate("/index/promotions"),
          visible: hasPermission(permissions, "Enrollment", "create"),
        },
        {
          label: "Parents",
          icon: "pi pi-fw pi-id-card",
          path: "/index/parents",
          command: () => navigate("/index/parents"),
          visible: hasPermission(permissions, "Student", "read"),
        },
      ],
    },

    // Daily Operations - Frequently used by office staff
    {
      label: "Daily Operations",
      icon: "pi pi-fw pi-calendar",
      visible: hasPermission(permissions, "Attendance", "read") || hasPermission(permissions, "Leave", "read") || hasPermission(permissions, "FeeVoucher", "read"),
      items: [
        {
          label: "Attendance",
          icon: "pi pi-fw pi-check-square",
          path: "/index/attendance",
          command: () => navigate("/index/attendance"),
          visible: hasPermission(permissions, "Attendance", "read"),
        },
        {
          label: "Leaves",
          icon: "pi pi-fw pi-calendar-minus",
          path: "/index/leaves",
          command: () => navigate("/index/leaves"),
          visible: hasPermission(permissions, "Leave", "read"),
        },
        {
          label: "Fee Vouchers",
          icon: "pi pi-fw pi-wallet",
          path: "/index/fee-vouchers",
          command: () => navigate("/index/fee-vouchers"),
          visible: hasPermission(permissions, "FeeVoucher", "read"),
        },
        {
          label: "Attendance Report",
          icon: "pi pi-fw pi-chart-bar",
          path: "/index/attendance-report",
          command: () => navigate("/index/attendance-report"),
          visible: isTeacher || isAdmin || hasPermission(permissions, "Attendance", "read"),
        },
      ],
    },

    // Academic Setup - Less frequent but important
    {
      label: "Academics",
      icon: "pi pi-fw pi-book",
      visible:
        hasPermission(permissions, "Grade", "read") ||
        hasPermission(permissions, "Curriculum", "read") ||
        hasPermission(permissions, "Holiday", "read"),
      items: [
        {
          // Likewise gated on its own subject rather than Grade.
          label: "Session",
          icon: "pi pi-fw pi-calendar",
          path: "/index/sessions",
          command: () => navigate("/index/sessions"),
          visible: hasPermission(permissions, "Session", "read"),
        },
        {
          // Drives term week availability; teachers get read access too.
          label: "Holiday Calendar",
          icon: "pi pi-fw pi-calendar-times",
          path: "/index/holidays",
          command: () => navigate("/index/holidays"),
          visible: hasPermission(permissions, "Holiday", "read"),
        },
        {
          label: "Grades",
          icon: "pi pi-fw pi-list",
          path: "/index/grades",
          command: () => navigate("/index/grades"),
          visible: hasPermission(permissions, "Grade", "read"),
        },
        {
          label: "Subjects",
          icon: "pi pi-fw pi-bookmark",
          path: "/index/subjects",
          command: () => navigate("/index/subjects"),
          visible: hasPermission(permissions, "Grade", "read"),
        },
        {
          label: "Curriculum",
          icon: "pi pi-fw pi-sitemap",
          path: "/index/curriculum-subjects",
          command: () => navigate("/index/curriculum-subjects"),
          visible: hasPermission(permissions, "Curriculum", "read"),
        },
        {
          // Writes school-wide records and creates portal accounts — admins
          // only, mirroring the guard on /api/import.
          label: "Data Import",
          icon: "pi pi-fw pi-upload",
          path: "/index/import",
          command: () => navigate("/index/import"),
          visible: ["SuperAdmin", "Admin", "Management"].includes(effectiveRole),
        },
        {
          label: "Website",
          icon: "pi pi-fw pi-globe",
          path: "/index/website",
          command: () => navigate("/index/website"),
          visible: ["SuperAdmin", "Admin", "Management"].includes(effectiveRole),
        },
      ],
    },

    // Media Library — shared file store used by the logo, website and more.
    // Permission-gated like Accounting so it can be granted per role, rather
    // than a hardcoded role list the Roles screen can't reach.
    {
      label: "Media Library",
      icon: "pi pi-fw pi-images",
      path: "/index/media",
      command: () => navigate("/index/media"),
      visible: hasPermission(permissions, "Media", "read"),
    },

    // Accounting
    {
      label: "Accounting",
      icon: "pi pi-fw pi-calculator",
      visible: hasPermission(permissions, "Accounting", "read"),
      items: [
        {
          label: "Cash Book",
          icon: "pi pi-fw pi-wallet",
          path: "/index/accounting/cash-book",
          command: () => navigate("/index/accounting/cash-book"),
          visible: hasPermission(permissions, "Accounting", "read"),
        },
        {
          label: "Chart of Accounts",
          icon: "pi pi-fw pi-list",
          path: "/index/accounting/chart-of-accounts",
          command: () => navigate("/index/accounting/chart-of-accounts"),
          visible: hasPermission(permissions, "Accounting", "read"),
        },
        {
          label: "Journal Entries",
          icon: "pi pi-fw pi-pencil",
          path: "/index/accounting/journal-entries",
          command: () => navigate("/index/accounting/journal-entries"),
          visible: hasPermission(permissions, "Accounting", "read"),
        },
        {
          label: "Fiscal Years",
          icon: "pi pi-fw pi-calendar",
          path: "/index/accounting/fiscal-years",
          command: () => navigate("/index/accounting/fiscal-years"),
          visible: hasPermission(permissions, "Accounting", "manage"),
        },
        {
          label: "Trial Balance",
          icon: "pi pi-fw pi-chart-bar",
          path: "/index/accounting/trial-balance",
          command: () => navigate("/index/accounting/trial-balance"),
          visible: hasPermission(permissions, "Accounting", "read"),
        },
        {
          label: "General Ledger",
          icon: "pi pi-fw pi-align-left",
          path: "/index/accounting/general-ledger",
          command: () => navigate("/index/accounting/general-ledger"),
          visible: hasPermission(permissions, "Accounting", "read"),
        },
      ],
    },

    // Infrastructure
    {
      label: "Infrastructure",
      icon: "pi pi-fw pi-building",
      visible: hasPermission(permissions, "ClassRoom", "read") || hasPermission(permissions, "Attendance", "read"),
      items: [
        {
          // Gated on ClassRoom, not Grade. It read Grade before, which meant
          // the ClassRoom permission rows controlled nothing and the page was
          // reachable by anyone who could read grades.
          label: "Class Rooms",
          icon: "pi pi-fw pi-home",
          path: "/index/classroom",
          command: () => navigate("/index/classroom"),
          visible: hasPermission(permissions, "ClassRoom", "read"),
        },
        {
          label: "Schedule",
          icon: "pi pi-fw pi-clock",
          path: "/index/schedules",
          command: () => navigate("/index/schedules"),
          visible: hasPermission(permissions, "Attendance", "read"),
        },
      ],
    },

    // Administration - System management (Admin only)
    {
      label: "Administration",
      icon: "pi pi-fw pi-cog",
      visible: hasPermission(permissions, "User", "read") || hasPermission(permissions, "Role", "read") || hasPermission(permissions, "Setting", "read"),
      items: [
        {
          label: "Users",
          icon: "pi pi-fw pi-users",
          path: "/index/users",
          command: () => navigate("/index/users"),
          visible: hasPermission(permissions, "User", "read"),
        },
        {
          label: "Roles",
          icon: "pi pi-fw pi-shield",
          path: "/index/roles",
          command: () => navigate("/index/roles"),
          visible: hasPermission(permissions, "Role", "read"),
        },
        {
          label: "Settings",
          icon: "pi pi-fw pi-sliders-h",
          path: "/index/settings",
          command: () => navigate("/index/settings"),
          visible: hasPermission(permissions, "Setting", "read"),
        },
      ],
    },
   
    

    // {
    //   label: "Teachers",
    //   icon: "pi pi-fw pi-users",
    //   command: () => navigate("/index/teachers"),
    //   visible: hasPermission(permissions, "User", "read"),
    // },
    // {
    //   label: "Media",
    //   icon: "pi pi-camera",
    //   command: () => navigate("/index/media"),
    //   visible: hasPermission(permissions, "Media", "view"),
    // },
    // {
    //   label: "Areas",
    //   icon: "pi pi-map-marker",
    //   command: () => navigate("/index/areas"),
    //   visible: hasPermission(permissions, "Area", "read"),
    // },
    // {
    //   label: "Cities",
    //   icon: "pi pi-map",
    //   command: () => navigate("/index/cities"),
    //   visible: hasPermission(permissions, "City", "read"),
    // },
    // {
    //   label: "Cars",
    //   icon: "pi pi-car",
    //   command: () => navigate("/index/cars"),
    //   visible: hasPermission(permissions, "Car", "read"),
    // },
    // {
    //   label: "Makes",
    //   icon: "pi pi-building",
    //   command: () => navigate("/index/makes"),
    //   visible: hasPermission(permissions, "Make", "read"),
    // },
    // {
    //   label: "Models",
    //   icon: "pi pi-sliders-h",
    //   command: () => navigate("/index/models"),
    //   visible: hasPermission(permissions, "Model", "read"),
    // },
    // {
    //   label: "Body Types",
    //   icon: "pi pi-car",
    //   command: () => navigate("/index/body-types"),
    //   visible: hasPermission(permissions, "BodyType", "read"),
    // },
    // {
    //   label: "Colors",
    //   icon: "pi pi-palette",
    //   command: () => navigate("/index/colors"),
    //   visible: hasPermission(permissions, "Color", "read"),
    // },
    // {
    //   label: "Features",
    //   icon: "pi pi-check-square",
    //   command: () => navigate("/index/features"),
    //   visible: hasPermission(permissions, "Feature", "read"),
    // },
    //  {
    //   label: "Attendance Reports",
    //   icon: "pi pi-chart-bar",
    //   command: () => navigate("/index/attendance-reports"),
    //   visible: hasPermission(permissions, "Attendance", "read"),
    //  },
    // {
    //   label: "Areas",
    //   icon: "pi pi-map-marker",
    //   command: () => navigate("/index/areas"),
    //   visible: hasPermission(permissions, "Area", "read"),
    // },
    // {
    //   label: "Cities",
    //   icon: "pi pi-map",
    //   command: () => navigate("/index/cities"),
    //   visible: hasPermission(permissions, "City", "read"),
    // },
    // {
    //   label: "Cars",
    //   icon: "pi pi-car",
    //   command: () => navigate("/index/cars"),
    //   visible: hasPermission(permissions, "City", "read"),
    // },
    // {
    //   label: "Makes",
    //   icon: "pi pi-building",
    //   command: () => navigate("/index/makes"),
    //   visible: hasPermission(permissions, "Make", "read"),
    // },
    // {
    //   label: "Models",
    //   icon: "pi pi-sliders-h",
    //   command: () => navigate("/index/models"),
    //   visible: hasPermission(permissions, "Model", "read"),
    // },
    // {
    //   label: "Body Types",
    //   icon: "pi pi-car",
    //   command: () => navigate("/index/body-types"),
    // },
    // {
    //   label: "Colors",
    //   icon: "pi pi-palette",
    //   command: () => navigate("/index/colors"),
    //   visible: hasPermission(permissions, "Color", "read"),
    // },
    // {
    //   label: "Features",
    //   icon: "pi pi-check-square",
    //   command: () => navigate("/index/features"),
    //   visible: hasPermission(permissions, "Feature", "read"),
    // },
    // {
    //   label: "Curriculum Subjects",
    //   icon: "pi pi-fw pi-list",
    //   command: () => navigate("/index/curriculum-subjects"),
    //   visible: hasPermission(permissions, "Setting", "read"),
    // },
  ];

  const userMenuItems = [
    {
      label: "Profile",
      icon: "pi pi-fw pi-user",
      // command: () => <Navigate to={'/profile'} />
      command: () => navigate("/index/profile"),
    },
    {
      label: "Logout",
      icon: "pi pi-fw pi-sign-out",
      command: logout,
    },
  ];
  // Display the real logged-in user (not the impersonated user) in navbar
  const displayUser = user;
  const displayName = displayUser?.name || "User";
  const displayRole = displayUser?.roleName || displayUser?.role?.name || "";
  const initials = displayName
    .split(" ")
    .map((w) => w[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  const end = (
    <div className="flex align-items-center gap-2">
      <NotificationBell />
      <Button
        icon={isDark ? "pi pi-sun" : "pi pi-moon"}
        onClick={toggleTheme}
        className="p-button-rounded p-button-text"
        aria-label="Toggle dark mode"
        tooltip={isDark ? "Light mode" : "Dark mode"}
        tooltipOptions={{ position: 'bottom' }}
      />
      <TieredMenu model={userMenuItems} popup ref={menuRef} />
      {/* User avatar chip — opens profile/logout menu */}
      <button
        onClick={(e) => menuRef.current.toggle(e)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: "0.5rem",
          padding: "0.35rem 0.75rem 0.35rem 0.35rem",
          border: "1px solid var(--surface-border)",
          borderRadius: "2rem",
          background: "var(--surface-card)",
          cursor: "pointer",
          outline: "none",
          fontFamily: "inherit",
          transition: "background 0.15s",
        }}
        onMouseEnter={(e) => (e.currentTarget.style.background = "var(--surface-hover)")}
        onMouseLeave={(e) => (e.currentTarget.style.background = "var(--surface-card)")}
      >
        <span
          style={{
            width: "2rem",
            height: "2rem",
            borderRadius: "50%",
            background: "#6366f1",
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "0.75rem",
            fontWeight: 700,
            flexShrink: 0,
          }}
        >
          {initials}
        </span>
        <span style={{ textAlign: "left" }}>
          <div style={{ fontSize: "0.8rem", fontWeight: 600, color: "var(--text-color)", lineHeight: 1.2 }}>
            {displayName}
          </div>
          {displayRole && (
            <div style={{ fontSize: "0.7rem", color: "var(--text-color-secondary)", lineHeight: 1.2 }}>
              {displayRole}
            </div>
          )}
        </span>
        <i className="pi pi-chevron-down" style={{ fontSize: "0.65rem", color: "var(--text-color-secondary)", marginLeft: "0.15rem" }} />
      </button>
    </div>
  );

  return (
    <div className="layout">
      <aside className="layout-sidebar pt-2">
        {/* In-app home. This used to point at "/", but React Router owns that
            path and renders the login page, so clicking your own logo looked
            like being signed out. The public site is a real page load, below. */}
        <Link to="/index/dashboard" className="flex justify-content-center w-full py-1">
          <img
            alt={schoolLogo ? "School logo" : "logo"}
            src={schoolLogo || "/site_logo.png"}
            height="80"
            className="logo"
            onError={(e) => { e.currentTarget.src = "/site_logo.png"; }}
          />
        </Link>
        {canSeeWebsite && (
          // Plain anchor, not <Link>: only a full page load reaches the
          // server-rendered public site instead of the SPA's "/" route.
          <a
            href="/"
            target="_blank"
            rel="noopener noreferrer"
            className="sidebar-public-link"
          >
            <i className="pi pi-external-link" /> View public site
          </a>
        )}
        {isSuperAdminImpersonating && (
          <>
            <div className="impersonation-banner">
              <div className="impersonation-banner-content">
                <i className="pi pi-eye"></i>
                <span className="impersonation-school-name">{impersonatedSchool.schoolName}</span>
              </div>
              <Button
                label="Exit School"
                icon="pi pi-sign-out"
                className="p-button-sm p-button-danger p-button-text"
                onClick={handleExitSchool}
              />
            </div>
            <ImpersonationBar />
            <TrialBanner />
          </>
        )}
        <SidebarMenu items={menuItems} />
      </aside>
      <header className="layout-header">
        <Menubar end={end} />
        <main className="layout-content">
          <Outlet />
        </main>
      </header>
    </div>
  );
};

export default Layout;
